import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { useChatContext } from '../../stores/ChatContext';
import { useAuth } from '../../auth/useAuth';
import { getSessions, deleteSession, renameSession } from '../../services/chatApi';
import type { ChatSession } from '@mem-ai-hub/shared';

export function AppShell() {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { setActiveSessionId } = useChatContext();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const fetchSessions = useCallback(async () => {
    try {
      const data = await getSessions(1, 50);
      setSessions(data.sessions);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSessions();
    }
  }, [isAuthenticated, fetchSessions]);

  const handleNewChat = () => {
    setActiveSessionId(null);
    navigate('/');
  };

  const handleSelectSession = (id: string) => {
    setActiveSessionId(id);
    navigate(`/chat/${id}`);
  };

  const handleDeleteSession = async (id: string) => {
    try {
      await deleteSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      // Navigate away if the deleted session was active
      if (window.location.pathname.includes(id)) {
        setActiveSessionId(null);
        navigate('/');
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  };

  const handleRenameSession = async (id: string, title: string) => {
    // Optimistic update
    setSessions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, title } : s)),
    );
    try {
      await renameSession(id, title);
    } catch (err) {
      console.error('Failed to rename session:', err);
      // Revert on failure
      fetchSessions();
    }
  };

  const sidebarSessions = sessions.map((s) => ({
    id: s.id,
    title: s.title,
    modelDisplayName: s.modelDisplayName,
    updatedAt: s.updatedAt,
  }));

  return (
    <div className="flex h-screen flex-col bg-gradient-mesh">
      <Header sidebarOpen={sidebarOpen} onToggleSidebar={() => setSidebarOpen((v) => !v)} />
      <div className="flex min-h-0 flex-1 pt-14">
        <Sidebar
          sessions={sidebarSessions}
          onNewChat={handleNewChat}
          onSelectSession={handleSelectSession}
          onDeleteSession={handleDeleteSession}
          onRenameSession={handleRenameSession}
          isOpen={sidebarOpen}
        />
        <main className={`flex flex-1 flex-col overflow-hidden px-6 py-3 transition-[margin] duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
          <Outlet context={{ refreshSessions: fetchSessions }} />
        </main>
      </div>
    </div>
  );
}
